import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  ArrowLeft, FileText, Users, Calendar, Clock, CheckCircle2,
  Circle, Sparkles, Save, AlertCircle, Brain,
  ThumbsUp, ThumbsDown, Pencil, AlertTriangle, Info, Search, Loader2,
  ListChecks, Target, Settings2, ClipboardCheck, Building2, FileSignature,
  ChevronRight, PenLine, Eye, EyeOff, BarChart3
} from 'lucide-react';

// Section editors - IEP
import CoverSheetEditor from '../components/iep-sections/CoverSheetEditor';
import SignaturesEditor from '../components/iep-sections/SignaturesEditor';
import PLAAFPEditor from '../components/iep-sections/PLAAFPEditor';
import ConsiderationsEditor from '../components/iep-sections/ConsiderationsEditor';
import EligibilityDeterminationEditor from '../components/met-sections/EligibilityDeterminationEditor';
import GoalsEditor from '../components/iep-sections/GoalsEditor';
import AccommodationsEditor from '../components/iep-sections/AccommodationsEditor';
import AssessmentEditor from '../components/iep-sections/AssessmentEditor';
import ServicesEditor from '../components/iep-sections/ServicesEditor';
import PWNEditor from '../components/iep-sections/PWNEditor';

// Section editors - MET
import METCoversheetEditor from '../components/met-sections/METCoversheetEditor';
import ReviewExistingDataEditor from '../components/met-sections/ReviewExistingDataEditor';
import PermissionToEvaluateEditor from '../components/met-sections/PermissionToEvaluateEditor';
import METPWNEditor from '../components/met-sections/METPWNEditor';
import EvaluationAssessmentsEditor from '../components/met-sections/EvaluationAssessmentsEditor';
import EvaluationSummaryEditor from '../components/met-sections/EvaluationSummaryEditor';
import PresentLevelsEditor from '../components/met-sections/PresentLevelsEditor';
import ObservationEditor from '../components/met-sections/ObservationEditor';
import InterpretationEditor from '../components/met-sections/InterpretationEditor';
import SummaryConclusionEditor from '../components/met-sections/SummaryConclusionEditor';
import EducationalImplicationsEditor from '../components/met-sections/EducationalImplicationsEditor';

import { API_URL } from '../utils/api';

const CASE_STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  in_progress: { label: 'In Progress', color: 'bg-blue-50 text-blue-700' },
  review: { label: 'Review', color: 'bg-amber-50 text-amber-700' },
  finalized: { label: 'Finalized', color: 'bg-green-50 text-green-700' },
  archived: { label: 'Archived', color: 'bg-gray-50 text-gray-500' },
};

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' }, { value: 'in_progress', label: 'In Progress' },
  { value: 'review', label: 'Review' }, { value: 'finalized', label: 'Finalized' },
];

const SECTION_STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' }, { value: 'draft', label: 'Draft' },
  { value: 'in_progress', label: 'In Progress' }, { value: 'complete', label: 'Complete' },
];

const SECTION_STATUS_ICON = { not_started: Circle, draft: Circle, in_progress: Clock, complete: CheckCircle2 };
const SECTION_STATUS_COLOR = { not_started: 'text-[#E5E5E5]', draft: 'text-gray-400', in_progress: 'text-blue-500', complete: 'text-green-600' };

// IEP sidebar section definitions with icons
const IEP_SIDEBAR = [
  { key: 'cover_sheet', label: 'Student Data', icon: FileText },
  { key: 'signatures', label: 'Signatures', icon: Users },
  { key: 'present_levels', label: 'Present Levels / PLAAFP', icon: PenLine },
  { key: 'considerations', label: 'Considerations', icon: ListChecks },
  { key: 'goals', label: 'Goals & Objectives', icon: Target },
  { key: 'accommodations', label: 'Accommodations', icon: Settings2 },
  { key: 'assessments', label: 'Assessment', icon: ClipboardCheck },
  { key: 'environment_services', label: 'Services & Environment', icon: Building2 },
  { key: 'pwn_summary', label: 'Prior Written Notice', icon: FileSignature },
  { key: 'finalize', label: 'Finalize', icon: CheckCircle2 },
];

// MET sidebar section definitions with icons
const MET_SIDEBAR = [
  { key: 'met_coversheet', label: 'Coversheet', icon: FileText },
  { key: 'met_review_data', label: 'Review of Existing Data', icon: Search },
  { key: 'met_present_levels', label: 'Present Levels', icon: BarChart3 },
  { key: 'met_permission', label: 'Permission To Evaluate', icon: FileSignature },
  { key: 'met_pwn_eval', label: 'PWN Evaluation', icon: FileSignature },
  { key: 'met_assessments', label: 'Evaluation Assessments', icon: ClipboardCheck },
  { key: 'met_observation', label: 'Observation: Testing Env', icon: Eye },
  { key: 'met_interpretation', label: 'Interpretation of Results', icon: Brain },
  { key: 'met_summary_conclusion', label: 'Summary / Conclusion', icon: FileText },
  { key: 'met_educational_implications', label: 'Educational Implications', icon: PenLine },
  // Legacy support: met_eval_summary for old cases
  { key: 'met_eval_summary', label: 'Evaluation Summary', icon: Brain, legacy: true },
  { key: 'met_eligibility', label: 'Eligibility Determination', icon: CheckCircle2 },
  { key: 'met_pwn_eligibility', label: 'PWN Eligibility', icon: FileSignature },
];

const CONFIDENCE_COLORS = {
  high: 'bg-green-50 text-green-700 border-green-200',
  moderate: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-gray-50 text-gray-600 border-gray-200',
};

export default function IEPMETCaseDetail() {
  const { caseId } = useParams();
  const { user } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const [saving, setSaving] = useState(false);
  // MET AI State
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [aiPreferences, setAiPreferences] = useState(null);
  // Carry-Forward State
  const [showCarryForward, setShowCarryForward] = useState(false);
  const [cfSources, setCfSources] = useState(null);
  const [cfPreview, setCfPreview] = useState(null);
  const [cfItemActions, setCfItemActions] = useState({});
  const [cfApplying, setCfApplying] = useState(false);
  const [cfResult, setCfResult] = useState(null);
  const [cfLoading, setCfLoading] = useState(false);
  // MET generic section state (for legacy/fallback only)
  const [sectionNotes, setSectionNotes] = useState('');

  const fetchCase = useCallback(async () => {
    try {
      const [caseRes, prefRes] = await Promise.all([
        axios.get(`${API_URL}/api/iep-met/cases/${caseId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/ai/preferences`, { withCredentials: true }).catch(() => null),
      ]);
      setCaseData(caseRes.data);
      if (prefRes) setAiPreferences(prefRes.data?.effective);
      if (!activeSection && caseRes.data.sections?.length > 0) {
        setActiveSection(caseRes.data.sections[0].section_key);
      }
      if (caseRes.data.case_type === 'met') {
        const analysesRes = await axios.get(`${API_URL}/api/iep-met/cases/${caseId}/analyses`, { withCredentials: true }).catch(() => null);
        if (analysesRes?.data?.length > 0) setAnalysisResult(analysesRes.data[0]);
      }
    } catch (err) {
      console.error('Failed to fetch case', err);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { fetchCase(); }, [fetchCase]);

  const handleCaseStatusChange = async (newStatus) => {
    try {
      const res = await axios.patch(`${API_URL}/api/iep-met/cases/${caseId}`, { status: newStatus }, { withCredentials: true });
      setCaseData(prev => ({ ...prev, ...res.data }));
    } catch (err) { console.error(err); }
  };

  const handleSectionStatusChange = async (sectionKey, newStatus) => {
    try {
      const res = await axios.patch(`${API_URL}/api/iep-met/cases/${caseId}/sections/${sectionKey}`, { status: newStatus }, { withCredentials: true });
      setCaseData(prev => ({ ...prev, sections: prev.sections.map(s => s.section_key === sectionKey ? { ...s, ...res.data } : s) }));
    } catch (err) { console.error(err); }
  };

  const handleSaveContent = async (sectionKey, content) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API_URL}/api/iep-met/cases/${caseId}/sections/${sectionKey}`, { content }, { withCredentials: true });
      setCaseData(prev => ({ ...prev, sections: prev.sections.map(s => s.section_key === sectionKey ? { ...s, ...res.data } : s) }));
    } catch (err) { console.error(err); } finally { setSaving(false); }
  };

  // MET generic save
  const handleSaveMETSection = async (sectionKey) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API_URL}/api/iep-met/cases/${caseId}/sections/${sectionKey}`, { content: { notes: sectionNotes } }, { withCredentials: true });
      setCaseData(prev => ({ ...prev, sections: prev.sections.map(s => s.section_key === sectionKey ? { ...s, ...res.data } : s) }));
    } catch (err) { console.error(err); } finally { setSaving(false); }
  };

  // MET Analysis
  const handleRunAnalysis = async () => {
    setAnalyzing(true); setShowAnalysis(true);
    try {
      const res = await axios.post(`${API_URL}/api/iep-met/cases/${caseId}/analyze`, {}, { withCredentials: true });
      setAnalysisResult(res.data);
    } catch (err) { console.error('Analysis failed', err); } finally { setAnalyzing(false); }
  };

  const handleSuggestionAction = async (field, action, editedContent, reason) => {
    if (!analysisResult) return;
    try {
      await axios.post(`${API_URL}/api/iep-met/analyses/${analysisResult.analysis_id}/action`, { field, action, edited_content: editedContent, reason }, { withCredentials: true });
      setAnalysisResult(prev => ({ ...prev, user_actions: [...(prev.user_actions || []), { field, action, timestamp: new Date().toISOString() }] }));
    } catch (err) { console.error(err); }
  };

  // Carry-Forward
  const handleLoadCarryForwardSources = async () => {
    setCfLoading(true); setShowCarryForward(true);
    try {
      const res = await axios.get(`${API_URL}/api/iep-met/cases/${caseId}/carry-forward/sources`, { withCredentials: true });
      setCfSources(res.data);
    } catch (err) { console.error(err); } finally { setCfLoading(false); }
  };

  const handlePreviewCarryForward = async (metCaseId) => {
    setCfLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/iep-met/cases/${caseId}/carry-forward/preview?met_case_id=${metCaseId}`, { withCredentials: true });
      setCfPreview(res.data);
      const defaults = {};
      for (const item of res.data.items) { defaults[`${item.source_field}__${item.target_section}`] = { action: 'accepted', edited_content: null }; }
      setCfItemActions(defaults);
    } catch (err) { console.error(err); } finally { setCfLoading(false); }
  };

  const handleApplyCarryForward = async () => {
    if (!cfPreview) return;
    setCfApplying(true);
    try {
      const items = cfPreview.items.map(item => {
        const key = `${item.source_field}__${item.target_section}`;
        const decision = cfItemActions[key] || { action: 'rejected' };
        return { source_field: item.source_field, target_section: item.target_section, action: decision.action, edited_content: decision.edited_content };
      });
      const res = await axios.post(`${API_URL}/api/iep-met/cases/${caseId}/carry-forward`, { met_case_id: cfPreview.source_met_case_id, analysis_id: cfPreview.source_analysis_id, items }, { withCredentials: true });
      setCfResult(res.data);
      const caseRes = await axios.get(`${API_URL}/api/iep-met/cases/${caseId}`, { withCredentials: true });
      setCaseData(caseRes.data);
    } catch (err) { console.error(err); } finally { setCfApplying(false); }
  };

  // MET section notes sync
  useEffect(() => {
    if (caseData && activeSection && caseData.case_type === 'met') {
      const sec = caseData.sections?.find(s => s.section_key === activeSection);
      setSectionNotes(sec?.content?.notes || '');
    }
  }, [activeSection, caseData]);

  if (loading) {
    return (<div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center"><div className="w-10 h-10 border-2 border-[#3B6A9B] border-t-transparent rounded-full animate-spin" /></div>);
  }

  if (!caseData) {
    return (
      <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center p-6">
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-8 max-w-md text-center">
          <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-4" />
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C] mb-2">Case Not Found</h2>
          <Link to="/products/iep-met"><Button variant="outline" className="rounded-xl" data-testid="case-not-found-back">Back to Cases</Button></Link>
        </div>
      </div>
    );
  }

  const student = caseData.student || {};
  const sections = caseData.sections || [];
  const currentSection = sections.find(s => s.section_key === activeSection);
  const statusConf = CASE_STATUS_CONFIG[caseData.status] || CASE_STATUS_CONFIG.draft;
  const completeSections = sections.filter(s => s.status === 'complete').length;
  const typeLabel = caseData.case_type === 'iep' ? 'IEP' : 'MET';
  const isMET = caseData.case_type === 'met';
  const isIEP = caseData.case_type === 'iep';
  const output = analysisResult?.output || {};
  const userActions = analysisResult?.user_actions || [];
  const getActionForField = (field) => userActions.find(a => a.field === field);

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white border-b border-[#E5E5E5]">
        <div className="px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/products/iep-met" className="flex items-center gap-2 text-[#595959] hover:text-[#1C1C1C] transition-colors" data-testid="back-to-cases-btn">
                <ArrowLeft className="h-5 w-5" /><span className="text-sm font-medium hidden sm:inline">Cases</span>
              </Link>
              <div className="h-6 w-px bg-[#E5E5E5]" />
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-4 w-4 text-[#3B6A9B] shrink-0" />
                <span className="font-outfit font-semibold text-[#1C1C1C] truncate">{caseData.title}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isMET && (
                <Button variant="outline" className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={showAnalysis ? () => setShowAnalysis(false) : handleRunAnalysis}
                  disabled={analyzing} data-testid="analyze-evaluation-btn">
                  {analyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Brain className="h-4 w-4 mr-2" />}
                  {analyzing ? 'Analyzing...' : showAnalysis ? 'Hide Analysis' : analysisResult ? 'View Analysis' : 'Analyze Evaluation'}
                </Button>
              )}
              {isIEP && (
                <Button variant="outline" className="rounded-xl border-[#3B6A9B] text-[#3B6A9B] hover:bg-[#3B6A9B]/5"
                  onClick={showCarryForward ? () => setShowCarryForward(false) : handleLoadCarryForwardSources}
                  disabled={cfLoading} data-testid="import-from-met-btn">
                  {cfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowLeft className="h-4 w-4 mr-2 rotate-180" />}
                  {showCarryForward ? 'Hide Import' : 'Import from MET'}
                </Button>
              )}
              <Badge className={`${statusConf.color} text-xs`}>{statusConf.label}</Badge>
              <Select value={caseData.status} onValueChange={handleCaseStatusChange}>
                <SelectTrigger className="w-[140px] rounded-xl text-xs h-8" data-testid="case-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-72 bg-white border-r border-[#E5E5E5] min-h-[calc(100vh-4rem)] hidden lg:block">
          <div className="p-4 sticky top-0 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {/* Student info */}
            <div className="mb-6 p-4 bg-[#F3F4F1] rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-[#595959]" />
                <span className="text-sm font-medium text-[#1C1C1C]">{student.first_name} {student.last_name}</span>
              </div>
              {student.grade && <p className="text-xs text-[#595959] ml-6">Grade: {student.grade}</p>}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge className={`${isIEP ? 'bg-[#3B6A9B]/10 text-[#3B6A9B]' : 'bg-[#5A8A5A]/10 text-[#5A8A5A]'} text-xs`}>{typeLabel}</Badge>
                {isIEP && caseData.context_type && (
                  <Badge className="bg-purple-50 text-purple-700 text-xs capitalize">{caseData.context_type}</Badge>
                )}
                <span className="text-xs text-[#595959]">{completeSections}/{sections.length} complete</span>
              </div>
              {caseData.due_date && <div className="flex items-center gap-1 mt-2 text-xs text-[#595959]"><Calendar className="h-3 w-3" />Due: {caseData.due_date}</div>}
              {caseData.meeting_date && <div className="flex items-center gap-1 mt-1 text-xs text-[#595959]"><Clock className="h-3 w-3" />Meeting: {caseData.meeting_date}</div>}
              <a href={`/products/iep-met/students/${caseData.student_id}/timeline`}
                className="flex items-center gap-1.5 mt-2.5 text-xs text-[#3B6A9B] hover:underline"
                data-testid="case-detail-timeline-link">
                <Clock className="h-3 w-3" />View Student Timeline
              </a>
            </div>

            <p className="text-xs font-semibold text-[#595959] uppercase tracking-wider mb-3 px-2">Sections</p>

            {/* IEP grouped sidebar */}
            {isIEP && (
              <nav className="space-y-1">
                {IEP_SIDEBAR.map(item => {
                  const sec = sections.find(s => s.section_key === item.key);
                  const StatusIcon = sec ? (SECTION_STATUS_ICON[sec.status] || Circle) : Circle;
                  const statusColor = sec ? (SECTION_STATUS_COLOR[sec.status] || 'text-[#E5E5E5]') : 'text-[#E5E5E5]';
                  const isActive = activeSection === item.key && !showAnalysis && !showCarryForward;
                  const Icon = item.icon;
                  return (
                    <button key={item.key}
                      onClick={() => { setActiveSection(item.key); setShowAnalysis(false); setShowCarryForward(false); }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors text-left ${isActive ? 'bg-[#3B6A9B]/10 text-[#3B6A9B] font-medium' : 'text-[#595959] hover:bg-[#F3F4F1]'}`}
                      data-testid={`section-nav-${item.key}`}
                    >
                      <StatusIcon className={`h-4 w-4 shrink-0 ${isActive ? 'text-[#3B6A9B]' : statusColor}`} />
                      <span className="flex-1 truncate">{item.label}</span>
                      {sec?.ai_capable && <Sparkles className="h-3 w-3 text-amber-500 shrink-0" />}
                    </button>
                  );
                })}
              </nav>
            )}

            {/* MET structured sidebar */}
            {isMET && (
              <nav className="space-y-1">
                {sections.filter(s => s.section_key !== 'transfer_finalize').map(sec => {
                  const sidebarItem = MET_SIDEBAR.find(m => m.key === sec.section_key);
                  const label = sidebarItem?.label || sec.title;
                  const Icon = sidebarItem?.icon || FileText;
                  const StatusIcon = SECTION_STATUS_ICON[sec.status] || Circle;
                  const statusColor = SECTION_STATUS_COLOR[sec.status] || 'text-[#E5E5E5]';
                  const isActive = activeSection === sec.section_key && !showAnalysis && !showCarryForward;
                  return (
                    <button key={sec.section_key}
                      onClick={() => { setActiveSection(sec.section_key); setShowAnalysis(false); setShowCarryForward(false); }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors text-left ${isActive ? 'bg-[#5A8A5A]/10 text-[#5A8A5A] font-medium' : 'text-[#595959] hover:bg-[#F3F4F1]'}`}
                      data-testid={`section-nav-${sec.section_key}`}
                    >
                      <StatusIcon className={`h-4 w-4 shrink-0 ${isActive ? 'text-[#5A8A5A]' : statusColor}`} />
                      <span className="flex-1 truncate">{label}</span>
                      {sec.ai_capable && <Sparkles className="h-3 w-3 text-amber-500 shrink-0" />}
                    </button>
                  );
                })}
              </nav>
            )}

            {/* MET Analysis link */}
            {isMET && (
              <>
                <div className="h-px bg-[#E5E5E5] my-3" />
                <button onClick={() => { setShowAnalysis(true); if (!analysisResult) handleRunAnalysis(); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors text-left ${showAnalysis ? 'bg-amber-50 text-amber-700 font-medium' : 'text-[#595959] hover:bg-amber-50/50'}`}
                  data-testid="sidebar-ai-analysis">
                  <Brain className={`h-4 w-4 shrink-0 ${showAnalysis ? 'text-amber-600' : 'text-amber-400'}`} />
                  <span className="flex-1">AI Analysis</span>
                  {analysisResult?.status === 'completed' && <CheckCircle2 className="h-3 w-3 text-green-500" />}
                </button>
              </>
            )}

            {/* IEP Import from MET link */}
            {isIEP && (
              <>
                <div className="h-px bg-[#E5E5E5] my-3" />
                <button onClick={() => { setShowCarryForward(true); setShowAnalysis(false); if (!cfSources) handleLoadCarryForwardSources(); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors text-left ${showCarryForward ? 'bg-[#3B6A9B]/10 text-[#3B6A9B] font-medium' : 'text-[#595959] hover:bg-[#3B6A9B]/5'}`}
                  data-testid="sidebar-carry-forward">
                  <ArrowLeft className={`h-4 w-4 shrink-0 rotate-180 ${showCarryForward ? 'text-[#3B6A9B]' : 'text-[#595959]'}`} />
                  <span className="flex-1">Import from MET</span>
                  {cfResult && <CheckCircle2 className="h-3 w-3 text-green-500" />}
                </button>
              </>
            )}

            {/* Linked Documents */}
            {caseData.linked_documents?.length > 0 && (
              <>
                <div className="h-px bg-[#E5E5E5] my-3" />
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#595959] px-3 mb-2">Linked Documents</p>
                {caseData.linked_documents.map(ld => (
                  <a key={ld.document_id} href={`/products/iep-met/documents/${ld.document_id}`}
                    className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-[#595959] hover:bg-[#F3F4F1] transition-colors"
                    data-testid={`linked-doc-${ld.document_id}`}>
                    <FileText className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                    <span className="flex-1 truncate text-xs">{ld.title}</span>
                    <ChevronRight className="h-3 w-3 shrink-0 text-[#E5E5E5]" />
                  </a>
                ))}
              </>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8" data-testid="case-detail-content">
          {/* Mobile section selector */}
          <div className="lg:hidden mb-6">
            <Select value={showAnalysis ? '__analysis__' : (activeSection || '')} onValueChange={v => {
              if (v === '__analysis__') { setShowAnalysis(true); if (!analysisResult) handleRunAnalysis(); }
              else { setShowAnalysis(false); setShowCarryForward(false); setActiveSection(v); }
            }}>
              <SelectTrigger className="rounded-xl" data-testid="mobile-section-select"><SelectValue placeholder="Select section" /></SelectTrigger>
              <SelectContent>
                {isIEP && IEP_SIDEBAR.map(item => <SelectItem key={item.key} value={item.key}>{item.label}</SelectItem>)}
                {isMET && sections.map(sec => {
                  const sidebarItem = MET_SIDEBAR.find(m => m.key === sec.section_key);
                  return <SelectItem key={sec.section_key} value={sec.section_key}>{sidebarItem?.label || sec.title}</SelectItem>;
                })}
                {isMET && <SelectItem value="__analysis__">AI Analysis</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          {showAnalysis ? (
            <AnalysisPanel analyzing={analyzing} result={analysisResult} output={output} userActions={userActions}
              getActionForField={getActionForField} onAction={handleSuggestionAction} onRerun={handleRunAnalysis}
              intensity={aiPreferences?.ai_intensity || 'moderate'} />
          ) : showCarryForward ? (
            <CarryForwardPanel loading={cfLoading} sources={cfSources} preview={cfPreview} itemActions={cfItemActions}
              setItemActions={setCfItemActions} applying={cfApplying} result={cfResult}
              onSelectSource={handlePreviewCarryForward} onApply={handleApplyCarryForward}
              onReset={() => { setCfPreview(null); setCfResult(null); setCfItemActions({}); }} />
          ) : isIEP && currentSection ? (
            <IEPSectionRouter section={currentSection} caseData={caseData} saving={saving}
              onSave={(content) => handleSaveContent(currentSection.section_key, content)}
              onStatusChange={(status) => handleSectionStatusChange(currentSection.section_key, status)}
              sections={sections} />
          ) : isMET && currentSection ? (
            <METSectionRouter section={currentSection} caseData={caseData} saving={saving}
              onSave={(content) => handleSaveContent(currentSection.section_key, content)}
              onStatusChange={(status) => handleSectionStatusChange(currentSection.section_key, status)}
              sections={sections} />
          ) : (
            <div className="text-center py-12"><p className="text-[#595959]">Select a section from the sidebar.</p></div>
          )}
        </main>
      </div>
    </div>
  );
}


// ========= IEP Section Router =========
function IEPSectionRouter({ section, caseData, saving, onSave, onStatusChange, sections }) {
  // Fallback map for cases created before structured editor types were added
  const EDITOR_TYPE_MAP = {
    cover_sheet: 'cover_sheet', signatures: 'participant_grid', present_levels: 'plaafp',
    considerations: 'checklist_narrative', goals: 'goals', accommodations: 'accommodations',
    assessments: 'assessment', environment_services: 'services', pwn_summary: 'pwn', finalize: 'finalize',
  };
  const editorType = section?.editor_type || EDITOR_TYPE_MAP[section?.section_key];

  // Section status bar shown above each editor
  const StatusBar = () => (
    <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#F3F4F1]">
      <div className="flex items-center gap-2">
        <span className="text-xs text-[#595959]">Section Status:</span>
        <Select value={section.status} onValueChange={onStatusChange}>
          <SelectTrigger className="w-[130px] rounded-xl text-xs h-7" data-testid="section-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>{SECTION_STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {section.updated_by && (
        <p className="text-xs text-[#595959]">Last edited by {section.updated_by}{section.updated_at ? ` · ${new Date(section.updated_at).toLocaleDateString()}` : ''}</p>
      )}
    </div>
  );

  return (
    <div>
      <StatusBar />
      {editorType === 'cover_sheet' && <CoverSheetEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'participant_grid' && <SignaturesEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'plaafp' && <PLAAFPEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'checklist_narrative' && <ConsiderationsEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'goals' && <GoalsEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'accommodations' && <AccommodationsEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'assessment' && <AssessmentEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'services' && <ServicesEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'pwn' && <PWNEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'finalize' && <FinalizeView sections={sections} caseData={caseData} />}
      {!editorType && <GenericEditor section={section} onSave={onSave} saving={saving} />}
    </div>
  );
}


// ========= MET Section Router =========
function METSectionRouter({ section, caseData, saving, onSave, onStatusChange, sections }) {
  const MET_EDITOR_MAP = {
    met_cover_sheet: 'met_coversheet', met_review_data: 'met_review_data', met_present_levels: 'met_present_levels',
    met_permission: 'met_permission', met_pwn_eval: 'met_pwn', met_assessments: 'met_assessments',
    met_eval_summary: 'met_eval_summary', met_observation: 'met_observation', met_interpretation: 'met_interpretation',
    met_summary_conclusion: 'met_summary_conclusion', met_educational_implications: 'met_educational_implications',
    met_eligibility: 'met_eligibility', met_pwn_eligibility: 'met_pwn',
    // Transfer MET mappings
    transfer_coversheet: 'transfer_coversheet', transfer_file_review: 'transfer_file_review',
    transfer_completeness: 'transfer_completeness', transfer_summary: 'transfer_summary',
    transfer_ed_implications: 'met_educational_implications', transfer_eligibility: 'met_eligibility',
    transfer_pwn: 'met_pwn', transfer_finalize: 'finalize',
  };
  const editorType = section?.editor_type || MET_EDITOR_MAP[section?.section_key];
  const StatusBar = () => (
    <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#F3F4F1]">
      <div className="flex items-center gap-2">
        <span className="text-xs text-[#595959]">Section Status:</span>
        <Select value={section.status} onValueChange={onStatusChange}>
          <SelectTrigger className="w-[130px] rounded-xl text-xs h-7" data-testid="section-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>{SECTION_STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {section.updated_by && (
        <p className="text-xs text-[#595959]">Last edited by {section.updated_by}{section.updated_at ? ` · ${new Date(section.updated_at).toLocaleDateString()}` : ''}</p>
      )}
    </div>
  );
  return (
    <div>
      <StatusBar />
      {editorType === 'met_coversheet' && <METCoversheetEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'met_review_data' && <ReviewExistingDataEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'met_present_levels' && <PresentLevelsEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'met_permission' && <PermissionToEvaluateEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'met_pwn' && <METPWNEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'met_assessments' && <EvaluationAssessmentsEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'met_eval_summary' && <EvaluationSummaryEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'met_observation' && <ObservationEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'met_interpretation' && <InterpretationEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'met_summary_conclusion' && <SummaryConclusionEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'met_educational_implications' && <EducationalImplicationsEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'met_eligibility' && <EligibilityDeterminationEditor section={section} caseData={caseData} onSave={onSave} saving={saving} />}
      {editorType === 'transfer_coversheet' && <TransferChecklistEditor section={section} caseData={caseData} onSave={onSave} saving={saving} editorVariant="coversheet" />}
      {editorType === 'transfer_file_review' && <TransferChecklistEditor section={section} caseData={caseData} onSave={onSave} saving={saving} editorVariant="file_review" />}
      {editorType === 'transfer_completeness' && <TransferChecklistEditor section={section} caseData={caseData} onSave={onSave} saving={saving} editorVariant="completeness" />}
      {editorType === 'transfer_summary' && <TransferChecklistEditor section={section} caseData={caseData} onSave={onSave} saving={saving} editorVariant="summary" />}
      {editorType === 'finalize' && <FinalizeView sections={sections} caseData={caseData} />}
      {!editorType && <GenericEditor section={section} onSave={onSave} saving={saving} />}
    </div>
  );
}


// ========= Transfer MET Checklist Editor =========
function TransferChecklistEditor({ section, caseData, onSave, saving, editorVariant }) {
  const [content, setContent] = useState(section?.content || {});
  useEffect(() => { setContent(section?.content || {}); }, [section?.section_id]);

  const updateField = (key, value) => setContent(prev => ({ ...prev, [key]: value }));
  const updateNested = (parent, key, value) => {
    setContent(prev => ({ ...prev, [parent]: { ...prev[parent], [key]: value } }));
  };
  const updateListItem = (listKey, idx, field, value) => {
    setContent(prev => ({
      ...prev,
      [listKey]: (prev[listKey] || []).map((item, i) => i === idx ? { ...item, [field]: value } : item),
    }));
  };

  const isTransfer = caseData?.case_subtype === 'transfer';

  return (
    <div className="max-w-4xl space-y-6" data-testid={`transfer-editor-${editorVariant}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]">{section?.title}</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
          {isTransfer && <Badge className="mt-2 bg-amber-50 text-amber-700 text-[10px] border border-amber-200">Transfer Review</Badge>}
        </div>
        <Button onClick={() => onSave(content)} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2C5A8B] text-white rounded-xl shrink-0" data-testid="transfer-save-btn">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* Coversheet variant */}
      {editorVariant === 'coversheet' && (
        <div className="space-y-4">
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959]">Transfer Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[['sending_district', 'Sending District'], ['sending_state', 'Sending State'], ['transfer_date', 'Transfer Date', 'date'], ['receiving_school', 'Receiving School'], ['receiving_district', 'Receiving District']].map(([key, label, type]) => (
                <div key={key}>
                  <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">{label}</label>
                  <Input type={type || 'text'} value={content[key] || ''} onChange={e => updateField(key, e.target.value)} className="rounded-xl text-sm h-9" data-testid={`transfer-${key}`} />
                </div>
              ))}
            </div>
            <div>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Reason for Transfer</label>
              <Textarea value={content.reason_for_transfer || ''} onChange={e => updateField('reason_for_transfer', e.target.value)} rows={2} className="rounded-xl text-sm" />
            </div>
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959]">Prior Eligibility</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Prior Eligibility Category</label>
                <Input value={content.prior_eligibility_category || ''} onChange={e => updateField('prior_eligibility_category', e.target.value)} className="rounded-xl text-sm h-9" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Prior Eligibility Date</label>
                <Input type="date" value={content.prior_eligibility_date || ''} onChange={e => updateField('prior_eligibility_date', e.target.value)} className="rounded-xl text-sm h-9" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Notes</label>
              <Textarea value={content.notes || ''} onChange={e => updateField('notes', e.target.value)} rows={3} className="rounded-xl text-sm" />
            </div>
          </div>
        </div>
      )}

      {/* File Review variant */}
      {editorVariant === 'file_review' && (
        <div className="space-y-4">
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">Documents Received Checklist</h3>
            <div className="space-y-3">
              {(content.documents_received || []).map((doc, idx) => (
                <div key={doc.key} className="border border-[#E5E5E5] rounded-xl p-3" data-testid={`doc-received-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[#1C1C1C]">{doc.label}</span>
                    <div className="flex gap-2">
                      {['yes', 'no', 'partial'].map(val => (
                        <button key={val} type="button" onClick={() => updateListItem('documents_received', idx, 'received', val)}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-medium border transition-all ${
                            doc.received === val
                              ? val === 'yes' ? 'bg-green-500 text-white border-green-500' : val === 'no' ? 'bg-red-400 text-white border-red-400' : 'bg-amber-400 text-white border-amber-400'
                              : 'bg-white text-[#595959] border-[#E5E5E5] hover:bg-gray-50'
                          }`}>
                          {val === 'yes' ? 'Received' : val === 'no' ? 'Missing' : 'Partial'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Date received" type="date" value={doc.date || ''} onChange={e => updateListItem('documents_received', idx, 'date', e.target.value)} className="rounded-lg text-xs h-7" />
                    <Input placeholder="Notes..." value={doc.notes || ''} onChange={e => updateListItem('documents_received', idx, 'notes', e.target.value)} className="rounded-lg text-xs h-7" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Overall File Notes</label>
            <Textarea value={content.overall_file_notes || ''} onChange={e => updateField('overall_file_notes', e.target.value)} rows={3} className="rounded-xl text-sm" />
          </div>
        </div>
      )}

      {/* Component Completeness variant */}
      {editorVariant === 'completeness' && (
        <div className="space-y-4">
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">Required Components</h3>
            <div className="space-y-3">
              {(content.components || []).map((comp, idx) => (
                <div key={comp.key} className="border border-[#E5E5E5] rounded-xl p-3" data-testid={`component-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[#1C1C1C]">{comp.label}</span>
                    <div className="flex gap-2">
                      {['met', 'not_met', 'partial'].map(val => (
                        <button key={val} type="button" onClick={() => updateListItem('components', idx, 'status', val)}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-medium border transition-all ${
                            comp.status === val
                              ? val === 'met' ? 'bg-green-500 text-white border-green-500' : val === 'not_met' ? 'bg-red-400 text-white border-red-400' : 'bg-amber-400 text-white border-amber-400'
                              : 'bg-white text-[#595959] border-[#E5E5E5] hover:bg-gray-50'
                          }`}>
                          {val === 'met' ? 'Met' : val === 'not_met' ? 'Not Met' : 'Partial'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Input placeholder="Notes..." value={comp.notes || ''} onChange={e => updateListItem('components', idx, 'notes', e.target.value)} className="rounded-lg text-xs h-7" />
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5 space-y-4">
            <div>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Overall Determination</label>
              <Select value={content.overall_determination || undefined} onValueChange={v => updateField('overall_determination', v)}>
                <SelectTrigger className="rounded-xl text-sm h-9"><SelectValue placeholder="Select determination" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sufficient">Sufficient — No additional evaluation needed</SelectItem>
                  <SelectItem value="additional_needed">Additional Evaluation Needed</SelectItem>
                  <SelectItem value="insufficient">Insufficient — Full evaluation required</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {content.overall_determination === 'additional_needed' && (
              <div>
                <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Additional Evaluation Areas Needed</label>
                <Textarea value={content.additional_eval_areas || ''} onChange={e => updateField('additional_eval_areas', e.target.value)} rows={3} className="rounded-xl text-sm" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary variant */}
      {editorVariant === 'summary' && (
        <div className="space-y-4">
          {[
            ['evaluation_summary', 'Evaluation Summary', 'Summarize the key findings from the transferred evaluation...'],
            ['strengths_noted', 'Strengths Noted', 'Document student strengths identified in the outside evaluation...'],
            ['areas_of_concern', 'Areas of Concern', 'Document areas of concern identified...'],
            ['comparison_to_az_standards', 'Comparison to Arizona Standards', 'How does the outside evaluation compare to Arizona eligibility standards...'],
            ['recommendations', 'Recommendations', 'Team recommendations based on the transfer review...'],
          ].map(([key, label, placeholder]) => (
            <div key={key} className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-2">{label}</h3>
              <Textarea value={content[key] || ''} onChange={e => updateField(key, e.target.value)} placeholder={placeholder} rows={4} className="rounded-xl text-sm" />
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={() => onSave(content)} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2C5A8B] text-white rounded-xl" data-testid="transfer-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

// ========= Finalize View =========
function FinalizeView({ sections, caseData }) {
  const complete = sections.filter(s => s.status === 'complete').length;
  const total = sections.length;
  return (
    <div className="max-w-3xl space-y-6" data-testid="finalize-view">
      <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]">Finalize IEP</h2>
      <p className="text-sm text-[#595959]">Review completion status of all sections before finalizing.</p>
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-2 bg-[#F3F4F1] rounded-full overflow-hidden">
            <div className="h-full bg-[#3B6A9B] rounded-full transition-all" style={{ width: `${total > 0 ? (complete / total) * 100 : 0}%` }} />
          </div>
          <span className="text-sm font-medium text-[#1C1C1C]">{complete}/{total}</span>
        </div>
        <div className="space-y-2">
          {sections.filter(s => s.section_key !== 'finalize').map(s => {
            const Icon = SECTION_STATUS_ICON[s.status] || Circle;
            const color = SECTION_STATUS_COLOR[s.status] || 'text-[#E5E5E5]';
            return (
              <div key={s.section_key} className="flex items-center gap-3 text-sm">
                <Icon className={`h-4 w-4 ${color}`} />
                <span className={s.status === 'complete' ? 'text-[#1C1C1C]' : 'text-[#595959]'}>{s.title}</span>
                <span className="text-xs text-[#595959] ml-auto capitalize">{s.status.replace('_', ' ')}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ========= Generic Editor (fallback) =========
function GenericEditor({ section, onSave, saving }) {
  const [notes, setNotes] = useState(section?.content?.notes || '');
  useEffect(() => { setNotes(section?.content?.notes || ''); }, [section?.section_id]);
  return (
    <div className="max-w-3xl" data-testid="generic-editor">
      <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C] mb-2" data-testid="section-title">{section?.title}</h2>
      <p className="text-sm text-[#595959] mb-6">{section?.description}</p>
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6">
        <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder={`Enter ${section?.title?.toLowerCase()} content...`}
          rows={10} className="rounded-xl border-[#E5E5E5] mb-4" data-testid="section-notes-input" />
        <div className="flex justify-end">
          <Button onClick={() => onSave({ notes })} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl" data-testid="save-section-btn">
            <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}




// ========= AI Analysis Panel (MET only — kept from original) =========
function AnalysisPanel({ analyzing, result, output, userActions, getActionForField, onAction, onRerun, intensity }) {
  if (analyzing) {
    return (
      <div className="max-w-3xl flex flex-col items-center justify-center py-20">
        <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mb-4"><Brain className="h-8 w-8 text-amber-600 animate-pulse" /></div>
        <h3 className="font-outfit text-lg font-semibold text-[#1C1C1C] mb-2">Analyzing Evaluation Data</h3>
        <p className="text-sm text-[#595959] text-center max-w-sm">AI is reviewing evaluation data against jurisdiction-specific eligibility criteria.</p>
        <div className="mt-6"><Loader2 className="h-6 w-6 text-amber-500 animate-spin" /></div>
      </div>
    );
  }
  if (!result || result.status === 'failed') {
    return (
      <div className="max-w-3xl">
        <div className="bg-white border border-red-200 rounded-2xl p-8 text-center">
          <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-4" />
          <h3 className="font-outfit text-lg font-semibold text-[#1C1C1C] mb-2">Analysis Failed</h3>
          <p className="text-sm text-[#595959] mb-4">{output?.error || 'AI analysis could not be completed.'}</p>
          <Button variant="outline" className="rounded-xl" onClick={onRerun} data-testid="retry-analysis-btn"><Brain className="h-4 w-4 mr-2" />Retry</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-3xl space-y-6" data-testid="analysis-panel">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1"><Brain className="h-6 w-6 text-amber-600" /><h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]">AI Evaluation Analysis</h2></div>
          <p className="text-sm text-[#595959]">Jurisdiction: {result.input_snapshot?.jurisdiction?.name || 'Federal'} | Intensity: {intensity}</p>
        </div>
        <Button variant="outline" className="rounded-xl text-xs" onClick={onRerun} data-testid="rerun-analysis-btn"><Brain className="h-3 w-3 mr-1" />Re-analyze</Button>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3" data-testid="advisory-banner">
        <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" /><p className="text-sm text-blue-800">{output.advisory_note}</p>
      </div>
      {output.strengths_summary && <AnalysisCard field="strengths_summary" title="Strengths Summary" icon={<ThumbsUp className="h-5 w-5 text-green-600" />} content={output.strengths_summary} action={getActionForField('strengths_summary')} onAction={onAction} color="green" />}
      {output.needs_summary && <AnalysisCard field="needs_summary" title="Needs Summary" icon={<Search className="h-5 w-5 text-[#3B6A9B]" />} content={output.needs_summary} action={getActionForField('needs_summary')} onAction={onAction} color="blue" />}
      {output.educational_impact && <AnalysisCard field="educational_impact" title="Educational Impact" icon={<FileText className="h-5 w-5 text-amber-600" />} content={output.educational_impact} action={getActionForField('educational_impact')} onAction={onAction} color="amber" />}
      {output.eligibility_suggestions?.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-2xl p-6" data-testid="analysis-eligibility">
          <div className="flex items-center gap-2 mb-4"><AlertTriangle className="h-5 w-5 text-amber-600" /><h3 className="font-outfit text-base font-semibold text-[#1C1C1C]">Possible Eligibility Categories</h3></div>
          <p className="text-xs text-amber-700 mb-4 italic">Suggestions for team consideration, not determinations.</p>
          <div className="space-y-4">
            {output.eligibility_suggestions.map((sug, i) => {
              const conf = CONFIDENCE_COLORS[sug.confidence] || CONFIDENCE_COLORS.low;
              const fieldKey = `eligibility_suggestions[${i}]`;
              const act = getActionForField(fieldKey);
              return (
                <div key={i} className={`border rounded-xl p-4 ${conf}`} data-testid={`eligibility-${sug.category_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{sug.category_name}</span>
                      <Badge className={`text-[10px] ${conf}`}>{sug.confidence} confidence</Badge>
                    </div>
                    {!act && (
                      <div className="flex gap-1">
                        <button onClick={() => onAction(fieldKey, 'accepted')} className="p-1 rounded hover:bg-green-100" data-testid={`accept-${sug.category_id}`}><ThumbsUp className="h-3.5 w-3.5 text-green-600" /></button>
                        <button onClick={() => onAction(fieldKey, 'rejected', null, 'Not applicable')} className="p-1 rounded hover:bg-red-100" data-testid={`reject-${sug.category_id}`}><ThumbsDown className="h-3.5 w-3.5 text-red-500" /></button>
                      </div>
                    )}
                    {act && <Badge className={act.action === 'accepted' ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-red-100 text-red-700 text-[10px]'}>{act.action}</Badge>}
                  </div>
                  <p className="text-sm mb-2">{sug.explanation}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="text-xs text-[#595959] text-center pt-2">Analysis ID: {result.analysis_id} | Model: {result.model_used} | {new Date(result.created_at).toLocaleString()}</div>
    </div>
  );
}

function AnalysisCard({ field, title, icon, content, action, onAction, color }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(content);
  const borderColor = { green: 'border-green-200', blue: 'border-blue-200', amber: 'border-amber-200' }[color] || 'border-[#E5E5E5]';
  return (
    <div className={`bg-white border ${borderColor} rounded-2xl p-6`} data-testid={`analysis-${field}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">{icon}<h3 className="font-outfit text-base font-semibold text-[#1C1C1C]">{title}</h3></div>
        {!action && (
          <div className="flex gap-1">
            <button onClick={() => onAction(field, 'accepted')} className="p-1.5 rounded-lg hover:bg-green-50 transition" data-testid={`accept-${field}`}><ThumbsUp className="h-4 w-4 text-green-600" /></button>
            <button onClick={() => setEditing(!editing)} className="p-1.5 rounded-lg hover:bg-blue-50 transition" data-testid={`edit-${field}`}><Pencil className="h-4 w-4 text-blue-600" /></button>
            <button onClick={() => onAction(field, 'rejected', null, 'Not applicable')} className="p-1.5 rounded-lg hover:bg-red-50 transition" data-testid={`reject-${field}`}><ThumbsDown className="h-4 w-4 text-red-500" /></button>
          </div>
        )}
        {action && <Badge className={action.action === 'accepted' ? 'bg-green-100 text-green-700 text-xs' : action.action === 'edited' ? 'bg-blue-100 text-blue-700 text-xs' : 'bg-red-100 text-red-700 text-xs'}>{action.action}</Badge>}
      </div>
      {!editing ? <p className="text-sm text-[#595959] leading-relaxed">{content}</p> : (
        <div>
          <Textarea value={editText} onChange={e => setEditText(e.target.value)} rows={4} className="rounded-xl mb-2 text-sm" />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" className="rounded-lg text-xs bg-blue-600 text-white" onClick={() => { onAction(field, 'edited', editText); setEditing(false); }}>Save Edit</Button>
          </div>
        </div>
      )}
    </div>
  );
}


// ========= Carry-Forward Panel (IEP only — kept from original) =========
const CF_SECTION_LABELS = { present_levels: 'Present Levels', goals: 'Goals', services: 'Services', accommodations: 'Accommodations', lre: 'LRE', esy: 'ESY', pwn_summary: 'PWN/Summary' };

function CarryForwardPanel({ loading, sources, preview, itemActions, setItemActions, applying, result, onSelectSource, onApply, onReset }) {
  if (loading && !sources) {
    return (<div className="max-w-3xl flex flex-col items-center py-20"><Loader2 className="h-8 w-8 text-[#3B6A9B] animate-spin mb-4" /><p className="text-sm text-[#595959]">Loading MET evaluations...</p></div>);
  }
  if (result) {
    return (
      <div className="max-w-3xl" data-testid="cf-result">
        <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-4" />
          <h3 className="font-outfit text-lg font-semibold text-[#1C1C1C] mb-2">Carry-Forward Complete</h3>
          <p className="text-sm text-[#595959] mb-4">{result.items_applied} items applied to {result.sections_updated} sections. {result.items_rejected > 0 && `${result.items_rejected} items rejected.`}</p>
          <Button variant="outline" className="rounded-xl" onClick={onReset} data-testid="cf-import-another">Import Another</Button>
        </div>
      </div>
    );
  }
  if (!preview) {
    const metCases = sources?.met_cases || [];
    return (
      <div className="max-w-3xl" data-testid="cf-sources">
        <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C] mb-1">Import from MET Evaluation</h2>
        <p className="text-sm text-[#595959] mb-6">Select a completed MET evaluation to carry forward findings.</p>
        {metCases.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-8 text-center">
            <AlertCircle className="h-10 w-10 text-[#595959] mx-auto mb-4" />
            <p className="text-sm text-[#595959]">No MET analyses available. Complete a MET evaluation first.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {metCases.map(mc => (
              <button key={mc.case_id} onClick={() => onSelectSource(mc.case_id)}
                className="w-full bg-white border border-[#E5E5E5] rounded-2xl p-5 hover:shadow-md transition-all text-left" data-testid={`cf-source-${mc.case_id}`}>
                <div className="flex items-center justify-between">
                  <div><h4 className="font-outfit font-semibold text-[#1C1C1C]">{mc.title}</h4><p className="text-xs text-[#595959] mt-1">{mc.available_analyses?.length || 0} analyses available</p></div>
                  <ChevronRight className="h-5 w-5 text-[#E5E5E5]" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
  // Review
  const items = preview.items || [];
  const allDecided = items.every(item => { const key = `${item.source_field}__${item.target_section}`; return itemActions[key]?.action; });
  const acceptedCount = Object.values(itemActions).filter(a => a.action === 'accepted' || a.action === 'edited').length;
  return (
    <div className="max-w-3xl space-y-6" data-testid="cf-review">
      <div className="flex items-start justify-between">
        <div><h2 className="font-outfit text-xl font-semibold text-[#1C1C1C] mb-1">Review Carry-Forward Items</h2><p className="text-sm text-[#595959]">{items.length} items mapped.</p></div>
        <Button variant="outline" className="rounded-xl text-xs" onClick={onReset}>Back</Button>
      </div>
      {Object.entries(items.reduce((acc, item) => { const s = item.target_section; if (!acc[s]) acc[s] = []; acc[s].push(item); return acc; }, {})).map(([section, sectionItems]) => (
        <div key={section}>
          <h3 className="font-outfit text-base font-semibold text-[#1C1C1C] mb-3">{CF_SECTION_LABELS[section] || section}</h3>
          <div className="space-y-3">
            {sectionItems.map(item => {
              const key = `${item.source_field}__${item.target_section}`;
              const decision = itemActions[key] || { action: 'accepted' };
              return <CFItemCard key={key} item={item} decision={decision} onDecision={(action, ec) => setItemActions(prev => ({ ...prev, [key]: { action, edited_content: ec } }))} />;
            })}
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between pt-4 border-t border-[#E5E5E5]">
        <p className="text-sm text-[#595959]">{acceptedCount}/{items.length} items will be applied</p>
        <Button className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl px-6" onClick={onApply} disabled={applying || !allDecided} data-testid="cf-apply-btn">
          {applying ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Applying...</> : <><CheckCircle2 className="h-4 w-4 mr-2" />Apply</>}
        </Button>
      </div>
    </div>
  );
}

function CFItemCard({ item, decision, onDecision }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(item.original_content);
  const isRejected = decision.action === 'rejected';
  const isEdited = decision.action === 'edited';
  return (
    <div className={`border rounded-xl p-4 transition-all ${isRejected ? 'border-red-200 bg-red-50/50 opacity-60' : isEdited ? 'border-blue-200 bg-blue-50/30' : 'border-green-200 bg-green-50/30'}`} data-testid={`cf-item-${item.source_field}-${item.target_section}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-[#1C1C1C]">{item.label}</span>
        <div className="flex gap-1">
          <button onClick={() => onDecision('accepted')} className={`p-1.5 rounded-lg transition ${decision.action === 'accepted' ? 'bg-green-100' : 'hover:bg-green-50'}`}><ThumbsUp className="h-4 w-4 text-green-600" /></button>
          <button onClick={() => setEditing(!editing)} className={`p-1.5 rounded-lg transition ${isEdited ? 'bg-blue-100' : 'hover:bg-blue-50'}`}><Pencil className="h-4 w-4 text-blue-600" /></button>
          <button onClick={() => onDecision('rejected')} className={`p-1.5 rounded-lg transition ${isRejected ? 'bg-red-100' : 'hover:bg-red-50'}`}><ThumbsDown className="h-4 w-4 text-red-500" /></button>
        </div>
      </div>
      {!editing ? (
        <div className="text-sm text-[#595959] leading-relaxed whitespace-pre-wrap max-h-32 overflow-y-auto bg-white/50 rounded-lg p-3">{isEdited && decision.edited_content ? decision.edited_content : item.original_content}</div>
      ) : (
        <div>
          <Textarea value={editText} onChange={e => setEditText(e.target.value)} rows={4} className="rounded-xl mb-2 text-sm" />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" className="rounded-lg text-xs bg-blue-600 text-white" onClick={() => { onDecision('edited', editText); setEditing(false); }}>Save</Button>
          </div>
        </div>
      )}
    </div>
  );
}
